import { OptionsConfiguration } from "../../types/customization/configuration";
import { Content, ContentPage } from "../../types/customization/content";
import { Template, TemplatePageDefinition } from "../../types/customization/template";
import {
  findPageDefinition,
  findPageDefinitionsWithValidCondition,
} from "../../utils/customization/finders";
import { generateNewPage } from "../../utils/customization/page";
import { countEmptyEditablePictures } from "./page";
import { findMinMaxEditablePicturesInPage, MinMax } from "./template";

export function countMinMaxTotalEmptyEditablePicturesInContent(
  template: Template,
  contentPages: ContentPage[]
): MinMax {
  let min = 0;
  let max = 0;

  for (const page of contentPages) {
    if (page.editablePictures.length > 0) {
      const emptySlots = countEmptyEditablePictures([page], template);
      min += emptySlots;
      max += emptySlots;
    } else {
      const minMaxInPage = findMinMaxEditablePicturesInPage(
        page.pageDefinitionKey,
        template.pageDefinitions
      );
      min += minMaxInPage.min;
      max += minMaxInPage.max;
    }
  }

  return { min, max };
}

export function countMinMaxTotalEditablePicturesInContent(
  template: Template,
  contentPages: ContentPage[]
): MinMax {
  let min = 0;
  let max = 0;

  for (const page of contentPages) {
    const minMaxInPage = findMinMaxEditablePicturesInPage(
      page.pageDefinitionKey,
      template.pageDefinitions
    );
    min += minMaxInPage.min;
    max += minMaxInPage.max;
  }

  return { min, max };
}

// check contentPageConfiguration and add required pages if missing in content
export function checkAndFillInitialPages(
  content: Content,
  template: Template,
  options?: OptionsConfiguration[]
): ContentPage[] {
  const requiredInitialPageFilters = template.contentPageConfiguration.forcedLayouts.map(
    ({ filterType }) => filterType
  );
  const initialPageFilters = content.pages.map((page) => {
    const pageDefinition = findPageDefinition(page.pageDefinitionKey, template);
    return pageDefinition.filterType;
  });

  const unusedPageFilters = [...requiredInitialPageFilters];
  for (const pageFilter of initialPageFilters) {
    if (unusedPageFilters.includes(pageFilter)) {
      unusedPageFilters.splice(unusedPageFilters.indexOf(pageFilter), 1);
    }
  }

  for (const missingFilterType of unusedPageFilters) {
    const pageDefinitions = findPageDefinitionsWithValidCondition(template, content, {}, options);
    const pageDefinition = pageDefinitions.find(
      (definition) => definition.filterType === missingFilterType
    );
    if (!pageDefinition) {
      continue;
    }
    const newPage = generateNewPage(pageDefinition.key, template);
    content.pages.push(newPage);
  }

  return content.pages;
}

export function getEmptyPageKeys(
  contentPages: ContentPage[],
  pageDefinitions: TemplatePageDefinition[]
): string[] {
  const emptyPages: string[] = [];

  contentPages.map((page) => {
    const pageDefinition = findPageDefinition(page.pageDefinitionKey, { pageDefinitions });

    let hasEditablePictureDefinition = false;
    let hasEditableTextDefinition = false;
    pageDefinition.elementDefinitions.map((element) => {
      if (element.type === "editable-picture") {
        hasEditablePictureDefinition = true;
      }
      if (element.type === "editable-text") {
        hasEditableTextDefinition = true;
      }
    });

    if (
      page.editablePictures.length + page.editableTexts.length === 0 &&
      (hasEditablePictureDefinition || hasEditableTextDefinition)
    ) {
      emptyPages.push(page.key);
    }
  });

  return emptyPages;
}

export function getNotEmptyPages(contentPages: ContentPage[]): ContentPage[] {
  const notEmptyPages: ContentPage[] = [];

  contentPages.map((page) => {
    if (page.editablePictures.length + page.editableTexts.length > 0) {
      notEmptyPages.push(page);
    }
  });

  return notEmptyPages;
}
